import {useFreightContext} from 'components/customer/Confirmation/Freight';
import {DispatchMethods} from 'components/customer/Confirmation/Freight/DispatchMethods';
import {lightenColor} from 'components/customer/Confirmation/helpers/lightenColor';
import {Depot as DepotInterface} from 'components/manufacturer/DepotSettings/depotApi';
import {useJobFormSubmitHandler} from 'hooks';
import React, {Dispatch, SetStateAction, useCallback, useMemo} from 'react';
import {Badge} from 'react-bootstrap';
import {useParams} from 'react-router-dom';
import {Icon} from 'shared/helpers';
import {useIsMobileLandscapeAndPortraitDevices} from 'shared/helpers/DeviceSize';
import {DepotFreightOptions} from 'store/customer/depotSlice';
import styled, {css} from 'styled-components';

interface DepotProps {
    depot: DepotInterface;
    activeDepot?: DepotInterface;
    setActiveDepot?: Dispatch<SetStateAction<DepotInterface>>;
}

export const Depot = ({depot}: DepotProps) => {
    const isMobileDevice = useIsMobileLandscapeAndPortraitDevices();
    const {jobId} = useParams();
    const {selectedDepot, setSelectedDepot} = useFreightContext();
    const {loadingSave: saving} = useJobFormSubmitHandler(
        jobId,
        undefined,
        true
    );

    const handleClick = useCallback(() => {
        if (setSelectedDepot) {
            setSelectedDepot(depot);
        }
    }, [setSelectedDepot, depot]);

    const deliveryOptions = useMemo(() => {
        if (depot) {
            switch (depot.freight_pickup_option) {
                case DepotFreightOptions.DELIVERY:
                    return [
                        {
                            label: 'Delivery only',
                            variant: 'primary',
                            icon: 'Dispatch-Delivery.svg',
                        },
                    ];
                    break;
                case DepotFreightOptions.PICKUP:
                    return [
                        {
                            label: 'Pickup only',
                            variant: 'secondary',
                            icon: 'Dispatch-Pickup.svg',
                        },
                    ];
                    break;
                case DepotFreightOptions.PICKUP_AND_DELIVERY:
                    return [
                        {
                            label: 'Delivery',
                            variant: 'primary',
                            icon: 'Dispatch-Delivery.svg',
                        },
                        {
                            label: 'Pickup',
                            variant: 'secondary',
                            icon: 'Dispatch-Pickup.svg',
                        },
                    ];
                    break;
            }
        }

        return [];
    }, [depot]);

    return (
        <Container
            $disabled={saving}
            $active={selectedDepot?.id == depot?.id}
            onClick={handleClick}>
            <NameContainer>
                <Icon iconName="Button-Tick-Secondary.svg" color="secondary" />
                <Name>{depot.depot_name}</Name>
            </NameContainer>
            <Address>{depot.depot_address}</Address>
            <BadgeContainer>
                {deliveryOptions.map((deliveryOption) => (
                    <Badge
                        key={deliveryOption.label}
                        bg={deliveryOption.variant}>
                        <Icon iconName={deliveryOption.icon} />
                        {deliveryOption.label}
                    </Badge>
                ))}
            </BadgeContainer>

            {selectedDepot?.id == depot?.id && isMobileDevice ? (
                <DispatchMethods />
            ) : null}
        </Container>
    );
};

const ActiveStyle = css`
    border-color: ${({theme}) => theme.colors.secondary?.main};
    box-shadow:
        0 6px 16px rgba(0, 0, 0, 0.1),
        0 14px 32px rgba(0, 0, 0, 0.14);
    transform: translateY(-2px);
`;

const Container = styled.div<{$active: boolean; $disabled: boolean}>`
    border: 1px solid rgb(228, 226, 219);
    box-shadow:
        rgba(0, 0, 0, 0.06) 0px 1px 3px,
        rgba(0, 0, 0, 0.06) 0px 3px 10px;
    padding: 10px;
    border-radius: 10px;
    cursor: pointer;
    transition:
        box-shadow 180ms,
        transform 180ms;
    flex: 0 0 calc(25% - 1rem);

    &:hover,
    &:active,
    &:focus {
        ${ActiveStyle}
    }

    ${({$active}) => {
        if ($active) {
            return css`
                ${ActiveStyle}

                ${NameContainer} > img {
                    display: block;
                }
            `;
        }
    }}

    ${({$disabled}) => {
        if ($disabled) {
            return `
                opacity: 0.8;
            `;
        }
    }}
`;

const NameContainer = styled.div`
    display: flex;
    margin-bottom: 5px;
    gap: 10px;

    > img {
        width: 18px;
        display: none;
    }
`;

const Name = styled.div`
    font-size: 1em;
    font-weight: 500;
    color: rgb(75, 74, 74);
    background: none;
    padding: 0;
`;

const Address = styled.div`
    font-weight: 500;
    font-size: 0.85em;
    color: rgb(135 135 135);
    margin-bottom: 5px;
`;

export const BadgeContainer = styled.div`
    display: flex;
    gap: 10px;

    > .badge {
        border-radius: 4px;
        padding: 2px 5px;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 5px;

        > img {
            width: 20px;
        }
    }

    > .bg-primary {
        background-color: ${({theme}) => lightenColor(theme.colors.primary?.main, 90)} !important;
        border: 1px solid ${({theme}) => theme.colors.primary?.main};
        color: ${({theme}) => theme.colors.primary?.main};
    }

    > .bg-secondary {
        background-color: ${({theme}) => lightenColor(theme.colors.secondary?.main, 90)} !important;
        border: 1px solid ${({theme}) => theme.colors.secondary?.main};
        color: ${({theme}) => theme.colors.secondary?.main};
    }
`;
